import { formatDate } from '@angular/common';
import { DataTypeEnum, IconModel, RefProperty } from '@osos/shared/types';
import { isJsonString } from '../utils';

type DateDataTypeEnum =
  | DataTypeEnum.Time
  | DataTypeEnum.Date
  | DataTypeEnum.DateTime;

export class InvalidTypeError extends Error {
  constructor(message) {
    super(message);
    this.name = 'InvalidType';
  }
}

export abstract class CastHelper {
  expectedType: DataTypeEnum;

  abstract isValidType(arg: any): boolean;
  abstract getCastedValue(arg: any): any;

  constructor(expectedType?: DataTypeEnum) {
    if (expectedType) {
      this.expectedType = expectedType;
    }
  }

  cast(arg: any): any {
    if (this.isValidType(arg)) {
      return this.getCastedValue(arg);
    }
    return this.throwInvalidType(arg);
  }

  throwInvalidType(arg) {
    throw new InvalidTypeError(
      `the arg with ${arg} doesn't match the expected type ${this.expectedType}`,
    );
  }

  getDefaultValue(): any {
    return null;
  }
}

export class TextCastHelper extends CastHelper {
  constructor() {
    super(DataTypeEnum.Text);
  }

  isValidType(arg: any): boolean {
    return !arg || typeof arg === 'string';
  }

  getCastedValue(arg: any) {
    return arg.toString();
  }
}

export class NumberCastHelper extends CastHelper {
  constructor() {
    super(DataTypeEnum.Number);
  }

  isValidType(arg: any): boolean {
    return !arg || typeof arg === 'number' || !isNaN(arg);
  }

  getCastedValue(arg: any) {
    return arg as number;
  }
}

export class BooleanCastHelper extends CastHelper {
  constructor() {
    super(DataTypeEnum.Boolean);
  }

  isValidType(arg: any): boolean {
    return !arg || typeof arg === 'boolean';
  }

  getCastedValue(arg: any) {
    return !!arg;
  }

  override getDefaultValue() {
    return false;
  }
}

export class DateCastHelper extends CastHelper {
  constructor(expectedType: DateDataTypeEnum) {
    super(expectedType);
  }

  isValidType(arg: any): boolean {
    return !arg || !isNaN(Date.parse(arg));
  }
  getCastedValue(arg: any) {
    if (arg) {
      switch (this.expectedType) {
        case DataTypeEnum.Date:
          return formatDate(arg, 'yyyy-MM-dd', 'en-US');
        default:
          return arg;
      }
    }
    return arg;
  }
}

export class BlobCastHelper extends CastHelper {
  constructor() {
    super(DataTypeEnum.blob);
  }

  isValidType(arg: any): boolean {
    return !arg || typeof arg === 'object';
  }
  getCastedValue(arg: any) {
    return arg as object;
  }
}

export class LocalizedJsonCastHelper extends CastHelper {
  constructor() {
    super(DataTypeEnum.LocalizedJson);
  }

  isValidType(arg: any): boolean {
    return !arg || !isJsonString(arg);
  }
  getCastedValue(arg: any) {
    return arg;
  }
}

export class EntityCastHelper extends CastHelper {
  isValidType(arg: any): boolean {
    return this._isReferanceProperty(arg);
  }

  getCastedValue(arg: any): RefProperty | RefProperty[] {
    if (arg === null) return null;

    return Array.isArray(arg) ? (arg as RefProperty[]) : (arg as RefProperty);
  }

  private _isReferanceProperty(arg: any): boolean {
    return Array.isArray(arg)
      ? !arg?.length || arg[0].hasOwnProperty('dataEntity')
      : arg === null || arg.hasOwnProperty('dataEntity');
  }
}

export class IconCastHelper extends CastHelper {
  // for now we can't cast any value to icon unless it was icon
  isValidType(arg: any): boolean {
    return arg === null || arg === undefined || typeof arg === 'string';
  }

  getCastedValue(arg: any): IconModel {
    return arg;
  }
}

export class JsonCastHelper extends CastHelper {
  isValidType(arg: any): boolean {
    return !arg || classof(arg) === 'object';
  }

  getCastedValue(arg: any): any {
    return arg;
  }
}
export class MapCastHelper extends CastHelper {
  // for now we can't cast any value to Map unless it was Map
  isValidType(arg: any): boolean {
    return (
      arg === null || arg === undefined || Object.keys(arg).includes('geometry')
    );
  }

  getCastedValue(arg: any): IconModel {
    return arg;
  }
}
// TODO discuss with amer about converting this to service
export class CastingHelperFactory {
  static castingHelperRegistry: Map<DataTypeEnum, CastHelper> = new Map<
    DataTypeEnum,
    CastHelper
  >([
    [DataTypeEnum.Text, new TextCastHelper()],
    [DataTypeEnum.Date, new DateCastHelper(DataTypeEnum.Date)],
    [DataTypeEnum.DateTime, new DateCastHelper(DataTypeEnum.DateTime)],
    [DataTypeEnum.Time, new DateCastHelper(DataTypeEnum.Time)],
    [DataTypeEnum.LocalizedJson, new LocalizedJsonCastHelper()],
    [DataTypeEnum.blob, new BlobCastHelper()],
    [DataTypeEnum.Boolean, new BooleanCastHelper()],
    [DataTypeEnum.Number, new NumberCastHelper()],
    [DataTypeEnum.Entity, new EntityCastHelper()],
    [DataTypeEnum.Icon, new IconCastHelper()],
    [DataTypeEnum.GeoJson, new MapCastHelper()],
    [DataTypeEnum.Json, new JsonCastHelper()],
  ]);
  static getCastHelper(dataTypeEnum: DataTypeEnum) {
    if (this.castingHelperRegistry.get(dataTypeEnum)) {
      return this.castingHelperRegistry.get(dataTypeEnum);
    }
    throw new InvalidTypeError(`Invalid type of ${dataTypeEnum}`);
  }
}
function classof(o) {
  return Object.prototype.toString.call(o).slice(8, -1).toLowerCase();
}

/**
 * Get the default value of the data type
 * @param dataType the data type
 * @returns the default value of the data type
 */
export function getDefaultValueOfDataType(dataType: DataTypeEnum): any {
  if (!dataType) {
    return null;
  }

  return CastingHelperFactory.getCastHelper(dataType).getDefaultValue();
}
